import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Stack,
  MenuItem,
  Pagination,
  Card,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";

import TableChartIcon from "@mui/icons-material/TableChart";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SortIcon from "@mui/icons-material/Sort";
import CategoryIcon from "@mui/icons-material/Category";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import AssignmentIcon from "@mui/icons-material/Assignment";

import { useLocation, useParams, Outlet } from "react-router-dom";
import api from "../../utils/api";
import DownloadIcon from "@mui/icons-material/Download";
import WarningIcon from "@mui/icons-material/Warning";

import {
  fetchWorkOrders,
  fetchStatuses,
  fetchAssignableUsers,
  fetchWorkOrderTypes,
} from "./Utils/workOrders.api";

import {
  getAssignedUserName,
  getTypeName,
  getStatusInfo,
} from "./Utils/workOrder.utils";

import WorkOrderCard from "./WorkOrderCard";
import WorkOrderTable from "./WorkOrderTable";
import StatusFilterCards from "./StatusFilterCards";

/* ------------------------------------------------------------------ */
/* Shared styles                                                       */
/* ------------------------------------------------------------------ */
const pageCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const secondaryButtonSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  px: 2,
  borderColor: "rgba(47,99,173,0.18)",
  color: "#223142",
  whiteSpace: "nowrap",
  "&:hover": {
    borderColor: "#2f63ad",
    color: "#2f63ad",
    backgroundColor: "rgba(47,99,173,0.04)",
  },
};

const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "rgba(255,255,255,0.96)",
  },
};

/* ------------------------------------------------------------------ */
/* Neighborhood labels & helpers                                       */
/* ------------------------------------------------------------------ */
const GROUP_NAME_BY_CODE = {
  "EH CH HILL": "Estate Homes",
  "EH EDGMT": "Estate Homes",
  SV: "Sommerset Villas",
  SC: "Southmont Cove",
  SW: "Sutton Walk",
  "VILLA HMS": "Villa Homes",
  WF: "Waterford",
  WW: "Wedgewood",
};

const norm = (v) => (v ?? "").toString().trim();
const canon = (v) => norm(v).replace(/\s+/g, " ").toUpperCase();
const isRenter = (v) => /\bRENTER\b/i.test(v);

const groupNameFor = (raw) => {
  const c = canon(raw);
  return GROUP_NAME_BY_CODE[c] || norm(raw);
};

const groupIdFor = (raw) => canon(groupNameFor(raw));

/* ------------------------------------------------------------------ */
/* Filter Bar                                                          */
/* ------------------------------------------------------------------ */
const FilterBar = ({
  viewMode,
  setViewMode,
  searchTerm,
  setSearchTerm,
  filter,
  setFilter,
  sortOrder,
  setSortOrder,
  selectedType,
  setSelectedType,
  selectedNeighborhood,
  setSelectedNeighborhood,
  workOrderTypes,
  neighborhoodOptions,
  onReset,
  totalCount,
}) => {
  const hasActive =
    !!searchTerm ||
    !!filter.startDate ||
    !!filter.endDate ||
    !!selectedType ||
    !!selectedNeighborhood;

  const typeLabel =
    selectedType &&
    (getTypeName(workOrderTypes, selectedType) ||
      workOrderTypes.find((t) => String(t.id) === String(selectedType))?.name ||
      selectedType);

  const dateLabel =
    filter.startDate || filter.endDate
      ? `${filter.startDate || "…"}. ${filter.endDate || "…"}`
      : "";

  const neighborhoodLabel =
    selectedNeighborhood &&
    (neighborhoodOptions.find((n) => n.code === selectedNeighborhood)?.label ||
      selectedNeighborhood);

  return (
    <Card sx={{ ...pageCardSx, overflow: "hidden", mb: 3 }}>
      <Box
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
          pt: 2.25,
          pb: 1.5,
          borderBottom: "1px solid rgba(47,99,173,0.08)",
          backgroundColor: "rgba(47,99,173,0.02)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <AssignmentIcon sx={{ color: "#2f63ad" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                Work Orders
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Search, filter, and review work orders across the community.
            </Typography>
          </Box>

          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            sx={{
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid rgba(47,99,173,0.08)",
              "& .MuiToggleButton-root": {
                px: 1.75,
                border: "none",
                textTransform: "none",
                fontWeight: 600,
                color: "#5f6b7a",
              },
              "& .Mui-selected": {
                color: "#2f63ad !important",
                backgroundColor: "rgba(47,99,173,0.08)",
              },
            }}
          >
            <ToggleButton value="cards">
              <ViewModuleIcon sx={{ mr: 1 }} /> Cards
            </ToggleButton>
            <ToggleButton value="table">
              <TableChartIcon sx={{ mr: 1 }} /> Table
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              placeholder="Search by work order #, address, type, priority, assignee, status..."
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={filterFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Start"
              size="small"
              value={filter.startDate}
              onChange={(e) =>
                setFilter((f) => ({ ...f, startDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonthIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={filterFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="End"
              size="small"
              value={filter.endDate}
              onChange={(e) =>
                setFilter((f) => ({ ...f, endDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonthIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={filterFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Sort by"
              size="small"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              sx={filterFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SortIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="newest">Newest to Oldest</MenuItem>
              <MenuItem value="oldest">Oldest to Newest</MenuItem>
              <MenuItem value="order_id_asc">Work Order # Ascending</MenuItem>
              <MenuItem value="order_id_desc">Work Order # Descending</MenuItem>
              <MenuItem value="type_asc">Type (A→Z)</MenuItem>
              <MenuItem value="type_desc">Type (Z→A)</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Request Type"
              size="small"
              value={selectedType}
              onChange={(e) => setSelectedType(String(e.target.value))}
              sx={filterFieldSx}
              SelectProps={{ displayEmpty: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CategoryIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">
                <em>All Types</em>
              </MenuItem>
              {workOrderTypes
                .slice()
                .sort((a, b) =>
                  (a.name || a.type || "").localeCompare(b.name || b.type || "")
                )
                .map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name || t.type || t.id}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Neighborhood"
              size="small"
              value={selectedNeighborhood}
              onChange={(e) => setSelectedNeighborhood(e.target.value)}
              sx={filterFieldSx}
              SelectProps={{ displayEmpty: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">
                <em>All Neighborhoods</em>
              </MenuItem>
              {neighborhoodOptions.map(({ code, label }) => (
                <MenuItem key={code} value={code}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 1.5,
                flexDirection: { xs: "column", sm: "row" },
                mt: 0.5,
              }}
            >
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {searchTerm && (
                  <Chip
                    label={`Search: ${searchTerm}`}
                    onDelete={() => setSearchTerm("")}
                    size="small"
                    variant="outlined"
                  />
                )}
                {dateLabel && (
                  <Chip
                    label={`Date: ${dateLabel}`}
                    onDelete={() => setFilter({ startDate: "", endDate: "" })}
                    size="small"
                    variant="outlined"
                  />
                )}
                {selectedType && (
                  <Chip
                    label={`Type: ${typeLabel}`}
                    onDelete={() => setSelectedType("")}
                    size="small"
                    variant="outlined"
                  />
                )}
                {selectedNeighborhood && (
                  <Chip
                    label={`Neighborhood: ${neighborhoodLabel}`}
                    onDelete={() => setSelectedNeighborhood("")}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  {totalCount} result{totalCount === 1 ? "" : "s"}
                </Typography>

                {hasActive && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ClearRoundedIcon />}
                    onClick={onReset}
                    sx={secondaryButtonSx}
                  >
                    Reset
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

/* ------------------------------------------------------------------ */
/* Main Page                                                           */
/* ------------------------------------------------------------------ */
const WorkOrdersPage = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [filteredWorkOrders, setFilteredWorkOrders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [workOrderTypes, setWorkOrderTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("cards");
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [filter, setFilter] = useState({ startDate: "", endDate: "" });
  const [selectedType, setSelectedType] = useState("");
  const [selectedNeighborhood, setSelectedNeighborhood] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 18;
  const location = useLocation();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("newest");
  const [fetchError, setFetchError] = useState("");
  const [slaBreached, setSlaBreached] = useState(0);
  const [exporting, setExporting] = useState(false);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token") || "";
      const params = new URLSearchParams();
      if (filterStatuses.length === 1) params.set("status", filterStatuses[0]);
      if (selectedType) params.set("type_id", selectedType);
      const res = await fetch(`${window.location.origin}/api/work-orders/export/csv?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href=url; a.download=`wo-export-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { console.error("Export failed:", err); }
    finally { setExporting(false); }
  };

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location?.state?.reload) {
      loadInitialData();
      window.history.replaceState({}, document.title);
    }
  }, [location?.state?.reload]);

  useEffect(() => {
    applyFilters();
  }, [
    searchTerm,
    filterStatuses,
    filter,
    workOrders,
    selectedType,
    selectedNeighborhood,
    workOrderTypes,
    assignableUsers,
  ]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterStatuses, filter, selectedType, selectedNeighborhood, sortOrder]);

  const neighborhoodOptions = useMemo(() => {
    const map = new Map();
    for (const o of workOrders || []) {
      const raw = norm(o?.ClubMembershipTypeCode);
      if (!raw || isRenter(raw)) continue;

      const id = groupIdFor(raw);
      const label = groupNameFor(raw);
      map.set(id, { code: id, label });
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [workOrders]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [orders, statusList, users, types] = await Promise.all([
        fetchWorkOrders(),
        fetchStatuses(),
        fetchAssignableUsers(),
        fetchWorkOrderTypes(),
      ]);

      const data = (orders.data || []).filter((o) => o && o.order_id);
      setWorkOrders(data);
      setStatuses(statusList.data || []);
      setAssignableUsers(users.data || []);

      setWorkOrderTypes(
        (types.data || []).map((t) => ({
          ...t,
          id: String(t?.id ?? t?.workorder_type_id ?? t?.type_id),
          name: String(
            t?.name ??
              t?.workorder_type_name ??
              t?.type_name ??
              t?.title ??
              t?.display_name ??
              t?.Description ??
              t?.TypeName ??
              ""
          ),
        }))
      );

      const counts = {};
      for (const o of data) counts[o.status] = (counts[o.status] || 0) + 1;
      setStatusCounts(counts);
    } catch (error) {
      console.error("Failed to load work orders:", error);
      setFetchError("Failed to load work orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const filteredStatusCounts = useMemo(() => {
    const counts = {};
    for (const o of filteredWorkOrders) {
      counts[o.status] = (counts[o.status] || 0) + 1;
    }
    return counts;
  }, [filteredWorkOrders]);

  const handleResetFilters = () => {
    setSearchTerm("");
    setFilterStatuses([]);
    setFilter({ startDate: "", endDate: "" });
    setSortOrder("newest");
    setSelectedType("");
    setSelectedNeighborhood("");
  };

  const applyFilters = () => {
    let filtered = [...workOrders];

    const terms = searchTerm
      .toLowerCase()
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => t.replace(/^wo[-\s]?/, ""));

    if (terms.length > 0) {
      filtered = filtered.filter((order) => {
        const user =
          getAssignedUserName(assignableUsers, order.assign_to)?.toLowerCase() || "";

        const combined = [
          order.order_id,
          `wo-${order.order_id}`,
          order.property_id,
          order.fullname ?? "",
          order.description,
          order.created_at,
          order.priority ?? "",
          order.full_address ?? "",
          order.ClubMembershipTypeCode ?? "",
          user,
          order.status,
          getTypeName(workOrderTypes, order.workorder_type_id) ?? "",
          order.subtype_name ?? "",
          order.location_name ?? "",
        ]
          .map((x) => x?.toString().toLowerCase() || "")
          .join(" ");

        return terms.some((term) => combined.includes(term));
      });
    }

    if (filterStatuses.length) {
      filtered = filtered.filter((order) => filterStatuses.includes(order.status));
    }

    if (filter.startDate || filter.endDate) {
      filtered = filtered.filter((order) => {
        const date = String(order.created_at).slice(0, 10);
        return (
          (!filter.startDate || date >= filter.startDate) &&
          (!filter.endDate || date <= filter.endDate)
        );
      });
    }

    if (selectedType) {
      filtered = filtered.filter(
        (order) => String(order.workorder_type_id) === String(selectedType)
      );
    }

    if (selectedNeighborhood) {
      filtered = filtered.filter(
        (order) => groupIdFor(order?.ClubMembershipTypeCode) === selectedNeighborhood
      );
    }

    setFilteredWorkOrders(filtered);
  };

  const handleStatusFilter = (status) => {
    setFilterStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const sortedWorkOrders = [...filteredWorkOrders].sort((a, b) => {
    if (sortOrder === "newest") {
      return new Date(b.created_at) - new Date(a.created_at);
    } else if (sortOrder === "oldest") {
      return new Date(a.created_at) - new Date(b.created_at);
    } else if (sortOrder === "order_id_asc") {
      return Number(a.order_id) - Number(b.order_id);
    } else if (sortOrder === "order_id_desc") {
      return Number(b.order_id) - Number(a.order_id);
    } else if (sortOrder === "type_asc" || sortOrder === "type_desc") {
      const aType = (getTypeName(workOrderTypes, a.workorder_type_id) || "").toLowerCase();
      const bType = (getTypeName(workOrderTypes, b.workorder_type_id) || "").toLowerCase();
      const cmp = aType.localeCompare(bType);
      return sortOrder === "type_asc" ? cmp : -cmp;
    }
    return 0;
  });

  const paginatedWorkOrders = sortedWorkOrders.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  // Load SLA breach count
  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    api.get("/work-orders/sla-status").then(r => {
      setSlaBreached((r.data||[]).filter(x=>x.sla_status==="breached").length);
    }).catch(()=>{});
  }, []);

  if (id) return <Outlet />;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: "1200px", mx: "auto" }}>
      {fetchError && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{fetchError}</Alert>}

      {/* SLA breach banner */}
      {slaBreached > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb:2, borderRadius:"12px" }}>
          <strong>{slaBreached} work order{slaBreached!==1?"s":""}</strong> have exceeded their SLA target. Review and take action.
        </Alert>
      )}

      {/* Export button */}
      <Box sx={{ display:"flex", justifyContent:"flex-end", mb:1.5 }}>
        <Tooltip title="Export current view to CSV">
          <Button size="small" variant="outlined" startIcon={<DownloadIcon fontSize="small" />}
            onClick={handleExportCSV} disabled={exporting}
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600, borderColor:"rgba(47,99,173,0.3)", color:"#2f63ad" }}>
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </Tooltip>
      </Box>
      <FilterBar
        viewMode={viewMode}
        setViewMode={setViewMode}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filter={filter}
        setFilter={setFilter}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedNeighborhood={selectedNeighborhood}
        setSelectedNeighborhood={setSelectedNeighborhood}
        workOrderTypes={workOrderTypes}
        neighborhoodOptions={neighborhoodOptions}
        onReset={handleResetFilters}
        totalCount={filteredWorkOrders.length}
      />

      <Box sx={{ mb: 3 }}>
        <StatusFilterCards
          statuses={statuses}
          statusCounts={filteredStatusCounts}
          activeFilters={filterStatuses}
          onToggle={handleStatusFilter}
        />
      </Box>

      {viewMode === "cards" ? (
        <Box>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Card View
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
              Showing {filteredWorkOrders.length} work order
              {filteredWorkOrders.length === 1 ? "" : "s"}.
            </Typography>
          </Box>

          <Grid container spacing={3} alignItems="stretch">
            {paginatedWorkOrders.length === 0 ? (
              <Grid item xs={12}>
                <Typography sx={{ color: "#6b778c" }}>No work orders found.</Typography>
              </Grid>
            ) : (
              paginatedWorkOrders.map((order) => (
                <Grid item key={order.order_id} xs={12} sm={6} md={4}>
                  <WorkOrderCard
                    order={order}
                    getAssignedUserName={(id) =>
                      getAssignedUserName(assignableUsers, id)
                    }
                    getTypeName={(id) => getTypeName(workOrderTypes, id)}
                    getStatusInfo={(status) => getStatusInfo(statuses, status)}
                  />
                </Grid>
              ))
            )}
          </Grid>

          {filteredWorkOrders.length > itemsPerPage && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={Math.ceil(filteredWorkOrders.length / itemsPerPage)}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </Box>
      ) : (
        <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
          <Box
            sx={{
              px: { xs: 1.5, sm: 2, md: 3 },
              py: 2,
              borderBottom: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.02)",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Table View
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
              Showing {filteredWorkOrders.length} work order
              {filteredWorkOrders.length === 1 ? "" : "s"}.
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ minWidth: "1100px" }}>
                <WorkOrderTable
                  rows={filteredWorkOrders}
                  getAssignedUserName={(id) => getAssignedUserName(assignableUsers, id)}
                  getTypeName={(id) => getTypeName(workOrderTypes, id)}
                  getStatusInfo={(status) => getStatusInfo(statuses, status)}
                />
              </Box>
            </Box>
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default WorkOrdersPage;